import { useQuery } from '@tanstack/react-query';

import { useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATION_POLL_INTERVAL_MS } from 'app/constants/reconciliations';

import { fetchAdminNames } from 'app/hooks/useFetchReconciliationsPage';

import SalaryReconciliationsApiHandler from 'app/services/salaryReconciliationsApiHandler';

import { SalaryReconciliationRunRow } from 'app/types/salaryReconciliations';

import { isActiveReconciliationRun } from 'app/utils/reconciliations';

export const SALARY_RECONCILIATION_QUERY_KEY = 'fetchSalaryReconciliation';

/** Reads one salary accrual reconciliation run and who started it, refreshing while it is still queued or running. */
const useFetchSalaryReconciliation = (id?: string) => {
  const toast = useToast();

  return useQuery<Omit<SalaryReconciliationRunRow, 'companyNames'>, Error>(
    [SALARY_RECONCILIATION_QUERY_KEY, id],
    async () => {
      const run = await SalaryReconciliationsApiHandler.getReconciliation(id as string);
      if (run.automated) {
        return run;
      }

      const adminNames = await fetchAdminNames([run.triggeredBy]);
      return { ...run, triggeredByName: adminNames.get(run.triggeredBy) };
    },
    {
      enabled: !!id,
      refetchInterval: (data) =>
        isActiveReconciliationRun(data) ? RECONCILIATION_POLL_INTERVAL_MS : false,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, we failed to fetch the salary reconciliation. Please try again.',
        });
      },
    }
  );
};

export default useFetchSalaryReconciliation;
